import type { EditorHost } from '@blocksuite/affine/std';

import { validateClassicMermaid } from '../../../modules/code-block-preview-renderer/classic-mermaid';

export type MermaidAIIntent =
  | 'create'
  | 'modify'
  | 'repair'
  | 'simplify'
  | 'expand'
  | 'change-type';

export type MermaidAIRequest = {
  context?: string;
  currentSource?: string;
  instruction?: string;
  intent: MermaidAIIntent;
  locale: string;
  parserError?: string;
  targetType?: string;
};

export type MermaidAIResult =
  | {
      ok: true;
      repairAttempted: boolean;
      source: string;
    }
  | {
      ok: false;
      code:
        | 'aborted'
        | 'empty_response'
        | 'invalid_response'
        | 'request_failed'
        | 'timeout'
        | 'validation_failed';
      message: string;
      repairAttempted: boolean;
    };

export class MermaidAIRequestTimeoutError extends Error {
  constructor() {
    super('Mermaid AI request timed out.');
    this.name = 'MermaidAIRequestTimeoutError';
  }
}

export type MermaidAIRequestExecutor = (
  host: EditorHost,
  request: MermaidAIRequest,
  signal?: AbortSignal
) => Promise<string>;

export const MERMAID_AI_MAX_CONTEXT_LENGTH = 12_000;
export const MERMAID_AI_MAX_INSTRUCTION_LENGTH = 4_000;
export const MERMAID_AI_MAX_SOURCE_LENGTH = 50_000;
export const MERMAID_AI_MAX_ERROR_LENGTH = 400;
export const MERMAID_AI_MAX_REPAIR_ATTEMPTS = 1;
export const MERMAID_AI_REQUEST_TIMEOUT_MS = 60_000;

const SUPPORTED_START =
  /^(?:flowchart|graph|sequenceDiagram|classDiagram|erDiagram|stateDiagram-v2|stateDiagram|gantt)\b/i;

function bounded(value: string | undefined, max: number) {
  return value?.trim().slice(0, max) ?? '';
}

export function buildMermaidAIInput(request: MermaidAIRequest) {
  return JSON.stringify({
    intent: request.intent,
    locale: bounded(request.locale, 32) || 'en',
    context: bounded(request.context, MERMAID_AI_MAX_CONTEXT_LENGTH),
    instruction: bounded(
      request.instruction,
      MERMAID_AI_MAX_INSTRUCTION_LENGTH
    ),
    currentSource: bounded(request.currentSource, MERMAID_AI_MAX_SOURCE_LENGTH),
    parserError: bounded(request.parserError, MERMAID_AI_MAX_ERROR_LENGTH),
    targetType: bounded(request.targetType, 64),
  });
}

function extractFence(raw: string) {
  const matches = raw.matchAll(/```(?:mermaid)?\s*\n?([\s\S]*?)```/gi);
  for (const match of matches) {
    const candidate = match[1]?.trim();
    if (
      candidate &&
      candidate.split(/\r?\n/).some(line => SUPPORTED_START.test(line.trim()))
    ) {
      return candidate;
    }
  }
  return null;
}

export function normalizeMermaidAIResponse(raw: string) {
  const cleaned = raw.replace(/^\uFEFF/, '').trim();
  if (!cleaned) return '';

  const fenced = extractFence(cleaned);
  const candidate = fenced ?? cleaned;
  const lines = candidate.replaceAll('\r\n', '\n').split('\n');
  const start = lines.findIndex(line => SUPPORTED_START.test(line.trim()));
  if (start < 0) return '';

  const source = lines.slice(start).join('\n').trim();
  return source.slice(0, MERMAID_AI_MAX_SOURCE_LENGTH);
}

async function collectTextStream(stream: BlockSuitePresets.TextStream) {
  let result = '';
  for await (const chunk of stream) {
    result += chunk;
    if (result.length > MERMAID_AI_MAX_SOURCE_LENGTH * 2) {
      break;
    }
  }
  return result;
}

function timeoutSignal(parent?: AbortSignal) {
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(new MermaidAIRequestTimeoutError()),
    MERMAID_AI_REQUEST_TIMEOUT_MS
  );
  const abort = () => controller.abort(parent?.reason);
  parent?.addEventListener('abort', abort, { once: true });

  return {
    signal: controller.signal,
    dispose() {
      clearTimeout(timer);
      parent?.removeEventListener('abort', abort);
    },
  };
}

export async function requestMermaidAI(
  host: EditorHost,
  request: MermaidAIRequest,
  signal?: AbortSignal
) {
  const timed = timeoutSignal(signal);
  try {
    const { getAIRequestService } = await import('../runtime/request');
    const stream = (await getAIRequestService().executeAction(
      'generateMermaid',
      {
        input: buildMermaidAIInput(request),
        stream: true,
        signal: timed.signal,
        control: 'format-bar',
        where: 'ai-panel',
        models: [],
        host,
        docId: host.store.id,
        workspaceId: host.store.workspace.id,
        intent: request.intent,
      }
    )) as BlockSuitePresets.TextStream | undefined;
    if (!stream) return '';
    return await collectTextStream(stream);
  } catch (error) {
    if (timed.signal.aborted && !signal?.aborted) {
      const reason = timed.signal.reason;
      throw reason instanceof Error
        ? reason
        : new MermaidAIRequestTimeoutError();
    }
    throw error;
  } finally {
    timed.dispose();
  }
}

export async function validateNormalizedMermaid(raw: string) {
  const source = normalizeMermaidAIResponse(raw);
  if (!source) {
    return {
      valid: false as const,
      source: '',
      error: 'AI response does not contain Mermaid source.',
    };
  }

  const validation = await validateClassicMermaid({ code: source });
  return validation.valid
    ? { valid: true as const, source }
    : {
        valid: false as const,
        source,
        error: validation.error ?? 'Invalid Mermaid syntax.',
      };
}

export async function finalizeMermaidAIResult(
  host: EditorHost,
  raw: string,
  request: MermaidAIRequest,
  signal?: AbortSignal,
  executeRequest: MermaidAIRequestExecutor = requestMermaidAI
): Promise<MermaidAIResult> {
  if (signal?.aborted) {
    return {
      ok: false,
      code: 'aborted',
      message: 'Mermaid AI request was cancelled.',
      repairAttempted: false,
    };
  }

  let validation = await validateNormalizedMermaid(raw);
  if (validation.valid) {
    return { ok: true, source: validation.source, repairAttempted: false };
  }

  if (!validation.source) {
    return {
      ok: false,
      code: 'empty_response',
      message: validation.error,
      repairAttempted: false,
    };
  }

  try {
    const repairedRaw = await executeRequest(
      host,
      {
        ...request,
        intent: 'repair',
        currentSource: validation.source,
        parserError: validation.error,
        instruction:
          request.instruction ||
          'Repair the Mermaid syntax without changing the intended meaning.',
      },
      signal
    );
    validation = await validateNormalizedMermaid(repairedRaw);
  } catch (error) {
    const code = signal?.aborted
      ? 'aborted'
      : error instanceof MermaidAIRequestTimeoutError
        ? 'timeout'
        : 'request_failed';
    return {
      ok: false,
      code,
      message: error instanceof Error ? error.message : String(error),
      repairAttempted: true,
    };
  }

  if (!validation.valid) {
    return {
      ok: false,
      code: 'validation_failed',
      message: validation.error,
      repairAttempted: true,
    };
  }

  return {
    ok: true,
    source: validation.source,
    repairAttempted: true,
  };
}
